import { Box, Typography } from '@mui/material';
import clsx from 'clsx';
import { isEmpty, toString } from 'lodash';
import { NO_TASKS, PROCESS_MANAGEMENT } from 'common/constants/firms';
import { MAX_CHAR_LENGTH } from 'common/constants/general';
import { PERCENTAGE } from 'common/constants/gridType';
import {
  fullyDilutedOwnershipFormat,
  largeCurrencyFormat,
  twoDecimalPercentFormat,
  xStandardSuffixFormat,
} from 'common/formats/formats';
import { COLUMN_TYPES, getValueType } from 'common/types/summaryDefinitions';
import { ValueWithLinkViewer } from 'components';
import {
  FIRM_SUMMARY_PROCESS_MANAGEMENT_COLUMN_TYPE,
  FIRM_SUMMARY_SPREADSHEET_ALL_FUNDS,
  FIRM_SUMMARY_SPREADSHEET_ALLOW_NEGATIVE_VALUE,
  FIRM_SUMMARY_SPREADSHEET_ROW_SPAN,
  FIRM_SUMMARY_SPREADSHEET_TITLE_ALIAS,
  FIRM_SUMMARY_SPREADSHEET_TOOLTIP_CLASSNAME,
  FIRM_SUMMARY_SPREADSHEET_TOTAL_ALIAS,
} from 'pages/Firms/common/constants';
import { FundSelector, SortIndicator } from 'pages/Firms/components';
import { FirmSummaryCell } from 'pages/Firms/hooks/useFirmSummarySheet';
import { TaskCell } from 'pages/Funds/components';
import { generateTotalExpression, getExpression, getObjectValue } from 'utilities';
import { alphabetGenerator } from 'utilities/alphabet-utilities';
import { CellParserParams, CreateParserParams, CreateParserReturn } from './createParser.types';
import { getTotalPublishedValuationsExpression, handleDraftCells, handleParentCells } from './utils';

const cellParser = (params: CellParserParams & { columnType: string }): FirmSummaryCell => {
  const {
    columnType,
    columnTypeOrder,
    allNonDraftParentsExpression,
    allParentsExpression,
    alphabet,
    breakevenMultiple,
    colNumber,
    column,
    columnLegend,
    firmSummaryClassNames,
    fundsAvailable,
    key,
    isProcessManagementEnabled,
    measurementDateName,
    onProcessManagementTaskClick,
    row,
    rowNumber,
    selectedFund,
    setSelectedFund,
    firmGrossIRR,
    fundsGrossIRR,
    companiesGrossIRR,
    onSortToggle,
    sortKeys,
    tasksProgress,
    totalPublishedInvestedCapitalExpression,
    totalPublishedTotalValueExpression,
    investedCapitalIndex,
    totalValueIndex,
    isEnhancedMode,
  } = params;

  const { alias: rowAlias, data: rowData } = getObjectValue(row);
  const { value: columnCellValue, ...columnCellData } = getObjectValue(column[toString(rowAlias)]);

  // Creating cell from row and column
  const cell = { ...row, data: { ...rowData, ...columnCellData }, key, value: columnCellValue } as FirmSummaryCell;
  const { fundUrl } = getObjectValue(cell.data);

  const valueType = columnType ? getValueType(columnType) : null;
  let title = valueType?.displayName ?? columnType;
  const tooltip = valueType?.metadata?.description;
  const breakevenMultipleValue = Number(breakevenMultiple);

  if (
    columnType === COLUMN_TYPES.BREAKEVEN_EXIT_EQUITY_VALUE &&
    Number.isFinite(breakevenMultipleValue) &&
    breakevenMultipleValue !== 1
  ) {
    const formattedMultiple = `${breakevenMultipleValue}`.replace(/(\.\d*?[1-9])0+$/u, '$1').replace(/\.0$/u, '');
    title = `${title} (${formattedMultiple}x)`;
  }

  const totalValueColumnLegend = totalValueIndex >= 0 ? alphabet[totalValueIndex] : '';
  const investedCapitalColumnLegend = investedCapitalIndex >= 0 ? alphabet[investedCapitalIndex] : '';

  const totalValueCellKey = totalValueColumnLegend ? `${totalValueColumnLegend}${rowNumber}` : '';
  const investedCapitalCellKey = investedCapitalColumnLegend ? `${investedCapitalColumnLegend}${rowNumber}` : '';

  // MOIC = total value / invested capital
  const MOICExpression =
    totalValueCellKey && investedCapitalCellKey
      ? `IF(${investedCapitalCellKey}>0,${totalValueCellKey}/${investedCapitalCellKey},0)`
      : '';

  // MOIC Total = total published valuations total value / total published valuations invested capital
  const MOICExpressionTotal =
    totalPublishedInvestedCapitalExpression && totalPublishedTotalValueExpression
      ? `IF(${totalPublishedInvestedCapitalExpression}>0,${totalPublishedTotalValueExpression}/${totalPublishedInvestedCapitalExpression},0)`
      : '';

  // Generate the cell based on the rowAlias
  switch (rowAlias) {
    // Generate title cells
    case FIRM_SUMMARY_SPREADSHEET_TITLE_ALIAS: {
      const currentSortKey = sortKeys?.find(([col]) => col === columnType);
      const sortDirection = currentSortKey ? currentSortKey[1] : null;

      if (columnType === FIRM_SUMMARY_PROCESS_MANAGEMENT_COLUMN_TYPE) {
        cell.value = PROCESS_MANAGEMENT;
      } else if (onSortToggle && columnType !== COLUMN_TYPES.FUND_NAME) {
        cell.value = (
          <SortIndicator title={title} direction={sortDirection} onClick={() => onSortToggle(columnType)} />
        );
        cell.forceComponent = true;
      } else {
        cell.value = title;
      }

      if (tooltip) {
        cell.tooltipMessages = [...(cell.tooltipMessages ?? []), tooltip];
      }

      if (columnType === COLUMN_TYPES.FUND_NAME) {
        cell.className = clsx(cell.className, 'text-right');
        cell.forceComponent = true;
        cell.value = '';
        cell.valueViewer = () => (
          <FundSelector handleOnChange={setSelectedFund} options={fundsAvailable} value={selectedFund} />
        );
      }
      break;
    }

    // Generate total cells
    case FIRM_SUMMARY_SPREADSHEET_TOTAL_ALIAS:
      switch (columnType) {
        case COLUMN_TYPES.FUND_NAME:
        case COLUMN_TYPES.VALUATION_DATE:
        case COLUMN_TYPES.INITIAL_INVESTMENT_DATE:
        case COLUMN_TYPES.MOST_RECENT_INVESTMENT_DATE:
          break;

        case COLUMN_TYPES.INVESTED_CAPITAL:
          cell.expr = allParentsExpression;
          cell.format = largeCurrencyFormat;
          cell.gridType = 'number';
          break;

        case COLUMN_TYPES.REALIZED_VALUE:
        case COLUMN_TYPES.UNREALIZED_VALUE:
        case COLUMN_TYPES.TOTAL_VALUE:
          cell.expr = allNonDraftParentsExpression;
          cell.format = largeCurrencyFormat;
          cell.gridType = 'number';

          handleDraftCells({ cell, firmSummaryClassNames });
          break;

        case COLUMN_TYPES.GROSS_IRR:
          // Gross IRR is not calculated for total
          cell.format = twoDecimalPercentFormat({});
          cell.value = selectedFund === FIRM_SUMMARY_SPREADSHEET_ALL_FUNDS ? firmGrossIRR : fundsGrossIRR?.[selectedFund];
          cell.gridType = PERCENTAGE;
          break;

        case COLUMN_TYPES.MOIC:
          cell.expr = MOICExpressionTotal ? `=${MOICExpressionTotal}` : '';
          cell.format = xStandardSuffixFormat;
          cell.gridType = 'number';
          handleDraftCells({ cell, firmSummaryClassNames });
          break;

        default:
          break;
      }
      break;

    default:
      // Generate column cells
      switch (columnType) {
        case FIRM_SUMMARY_PROCESS_MANAGEMENT_COLUMN_TYPE:
          cell.gridType = 'string';

          if (isProcessManagementEnabled) {
            cell.component = tasksProgress?.companies?.length ? (
              <TaskCell
                cellKey={key}
                companyName={toString(cell.data?.companyName)}
                companySlug={toString(cell.data?.companySlug)}
                fundName={toString(cell.data?.fundName)}
                measurementDateName={toString(measurementDateName)}
                onTaskClick={onProcessManagementTaskClick}
                tasksProgress={tasksProgress}
              />
            ) : (
              <Box display="flex" justifyContent="center">
                <Typography variant="body2">{NO_TASKS}</Typography>
              </Box>
            );
            cell.forceComponent = true;
          }
          break;

        case COLUMN_TYPES.FUND_NAME:
          // Class to show tooltip if text is truncated
          cell.className = clsx(cell.className, 'text-right', FIRM_SUMMARY_SPREADSHEET_TOOLTIP_CLASSNAME);
          cell.forceComponent = true;
          cell.valueViewer = () => <ValueWithLinkViewer name={toString(cell.value)} url={fundUrl} />;
          break;

        case COLUMN_TYPES.VALUATION_DATE:
        case COLUMN_TYPES.INITIAL_INVESTMENT_DATE:
        case COLUMN_TYPES.MOST_RECENT_INVESTMENT_DATE:
          break;

        case COLUMN_TYPES.DILUTED_OWNERSHIP:
          cell.format = fullyDilutedOwnershipFormat;
          cell.gridType = 'percentage';

          if (!isEnhancedMode) handleParentCells({ cell, colNumber, rowNumber, columnType });
          break;

        case COLUMN_TYPES.OUTSTANDING_OWNERSHIP:
          cell.format = fullyDilutedOwnershipFormat;
          cell.gridType = 'percentage';
          break;

        case COLUMN_TYPES.INVESTED_CAPITAL:
        case COLUMN_TYPES.REALIZED_VALUE:
        case COLUMN_TYPES.UNREALIZED_VALUE:
        case COLUMN_TYPES.TOTAL_VALUE:
          cell.format = largeCurrencyFormat;
          cell.gridType = 'number';

          if (!isEnhancedMode) handleParentCells({ cell, colNumber, rowNumber, columnType });
          handleDraftCells({ cell, firmSummaryClassNames });
          break;

        case COLUMN_TYPES.GROSS_IRR:
          cell.format = twoDecimalPercentFormat({});
          cell.gridType = 'percentage';

          if (!isEnhancedMode) handleParentCells({ cell, colNumber, rowNumber, columnType, companiesGrossIRR });
          handleDraftCells({ cell, firmSummaryClassNames });
          break;

        case COLUMN_TYPES.MOIC:
          cell.expr = MOICExpression ? `=${MOICExpression}` : '';
          cell.format = xStandardSuffixFormat;
          cell.gridType = 'number';

          handleDraftCells({ cell, firmSummaryClassNames });
          break;

        case COLUMN_TYPES.CURRENT_FUND_VALUE:
        case COLUMN_TYPES.EQUITY_VALUE:
        case COLUMN_TYPES.ENTERPRISE_VALUE:
        case COLUMN_TYPES.TOTAL_INSIGHT_PREFERENCE:
        case COLUMN_TYPES.FUND_PREFERENCE:
        case COLUMN_TYPES.SPECIFIC_PREFERENCE:
        case COLUMN_TYPES.TOTAL_COMPANY_PREFERENCE:
        case COLUMN_TYPES.PROJECTED_REVENUE_YEAR_1:
        case COLUMN_TYPES.PROJECTED_REVENUE_YEAR_2:
        case COLUMN_TYPES.PROJECTED_REVENUE_YEAR_3:
          cell.format = largeCurrencyFormat;
          cell.gridType = 'number';
          break;

        case COLUMN_TYPES.BREAKEVEN_EXIT_EQUITY_VALUE:
        case COLUMN_TYPES.ALLOCATION_METHOD:
        case COLUMN_TYPES.ENTERPRISE_VALUATION_METHOD:
        case COLUMN_TYPES.VALUATION_APPROACH:
        case COLUMN_TYPES.VALUATION_STATUS:
          cell.gridType = 'string';
          break;

        case COLUMN_TYPES.PERCENT_CHANGE_FROM_PREVIOUS:
        case COLUMN_TYPES.DCF_DISCOUNT_RATE:
          cell.format = twoDecimalPercentFormat({});
          cell.gridType = 'percentage';
          break;

        case COLUMN_TYPES.IMPLIED_ARR_MULTIPLE:
          cell.format = xStandardSuffixFormat;
          cell.gridType = 'number';
          break;

        default:
          break;
      }
      break;
  }

  // Default values
  const {
    allowEmptyValues = true,
    allowNegativeValue = FIRM_SUMMARY_SPREADSHEET_ALLOW_NEGATIVE_VALUE,
    customKey = null,
    dbType = 'string',
    expr = '',
    format = null,
    gridType = 'string',
    maxNumberChars = MAX_CHAR_LENGTH,
    readOnly = true,
    rowSpan = FIRM_SUMMARY_SPREADSHEET_ROW_SPAN,
    tooltipMessages = [],
    value = '',
  } = getObjectValue(cell);

  return {
    ...cell,
    allowEmptyValues,
    allowNegativeValue,
    chartTitle: title,
    columnLegend,
    customKey,
    dbType,
    expr: getExpression({ expr: toString(expr), columnLegend }),
    format,
    gridType,
    key,
    maxNumberChars,
    readOnly,
    rowSpan,
    tooltipMessages,
    value,
  } as FirmSummaryCell;
};

const createParser = (params: CreateParserParams): CreateParserReturn => {
  const {
    columns,
    breakevenMultiple,
    firmSummaryClassNames,
    fundsAvailable,
    isProcessManagementEnabled,
    nonDraftParentFundsByCompanyRows,
    measurementDateName,
    onProcessManagementTaskClick,
    orderedColumnTypes,
    parentFundsByCompanyRows,
    rowConfig,
    selectedFund,
    setSelectedFund,
    firmGrossIRR,
    fundsGrossIRR,
    companiesGrossIRR,
    onSortToggle,
    sortKeys,
    tasksProgress,
    isEnhancedMode,
  } = params;

  const cells = {} as CreateParserReturn;

  const columnTypeOrder = orderedColumnTypes ?? [];
  const alphabet = alphabetGenerator([], columns.length);

  const allParentsTotalExpression = generateTotalExpression({
    items: parentFundsByCompanyRows,
    generateIndex: false,
  });
  const allParentsExpression = !isEmpty(allParentsTotalExpression) ? `=SUM(${allParentsTotalExpression})` : '';

  const allNonDraftParentsTotalExpression = generateTotalExpression({
    items: nonDraftParentFundsByCompanyRows,
    generateIndex: false,
  });
  const allNonDraftParentsExpression = !isEmpty(allNonDraftParentsTotalExpression)
    ? `=SUM(${allNonDraftParentsTotalExpression})`
    : '';

  const investedCapitalIndex = columnTypeOrder.indexOf(COLUMN_TYPES.INVESTED_CAPITAL);
  const totalValueIndex = columnTypeOrder.indexOf(COLUMN_TYPES.TOTAL_VALUE);

  const totalPublishedInvestedCapitalExpression = getTotalPublishedValuationsExpression({
    allPublishedValuationsExpression: allNonDraftParentsTotalExpression,
    alphabet,
    columnIndex: investedCapitalIndex,
  });

  const totalPublishedTotalValueExpression = getTotalPublishedValuationsExpression({
    allPublishedValuationsExpression: allNonDraftParentsTotalExpression,
    alphabet,
    columnIndex: totalValueIndex,
  });

  rowConfig.forEach((row, rowIndex: number) => {
    columns.forEach((column, colIndex: number) => {
      const rowNumber = rowIndex + 1;
      const colNumber = colIndex + 1;
      const columnLegend = alphabet[colIndex];
      const columnType = columnTypeOrder[colIndex] ?? '';

      const key = columnLegend + rowNumber;

      const cell = cellParser({
        allNonDraftParentsExpression,
        allParentsExpression,
        alphabet,
        breakevenMultiple,
        colNumber,
        column,
        columnLegend,
        columnType,
        columnTypeOrder,
        firmSummaryClassNames,
        fundsAvailable,
        isEnhancedMode,
        isProcessManagementEnabled,
        key,
        measurementDateName,
        onSortToggle,
        onProcessManagementTaskClick,
        row,
        rowNumber,
        selectedFund,
        setSelectedFund,
        sortKeys,
        tasksProgress,
        firmGrossIRR,
        fundsGrossIRR,
        companiesGrossIRR,
        totalPublishedInvestedCapitalExpression,
        totalPublishedTotalValueExpression,
        investedCapitalIndex,
        totalValueIndex,
      });

      if (
        isEnhancedMode &&
        row.alias !== FIRM_SUMMARY_SPREADSHEET_TOTAL_ALIAS &&
        columnType !== FIRM_SUMMARY_PROCESS_MANAGEMENT_COLUMN_TYPE
      ) {
        cell.showChart = true;
        cell.chartMetricKey = columnType;
        cell.chartEntityName = cell.data?.entityName;
      }

      cells[key] = cell;
    });
  });

  return cells;
};

export default createParser;
